import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { APP_STORE_URL, PLAY_STORE_URL } from '../constants/stores'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** Matches keepcool-backend signup USERNAME_REGEX */
const USERNAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_.]{1,19}$/

function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  )
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')

/** POST /api/signup → keepcool-backend controllers/signup.js signupWithEmail */
const SIGNUP_ENDPOINT = `${API_BASE_URL}/api/signup`

/**
 * Map API JSON body from signupWithEmail to a localized message.
 * See keepcool-backend/controllers/signup.js signupWithEmail
 */
function mapSignupApiError(data, t) {
  const msg = typeof data?.message === 'string' ? data.message : ''
  const err = typeof data?.error === 'string' ? data.error : ''
  const haystack = `${msg} ${err}`

  if (haystack.includes('Coach email is required')) {
    return t.coachInvite.signup.errors.coachEmailRequired
  }
  if (haystack.includes('Coach email is not found')) {
    return t.coachInvite.signup.errors.coachEmailNotFound
  }
  if (haystack.includes('Invitation token is not found')) {
    return t.coachInvite.signup.errors.inviteTokenInvalid
  }
  if (haystack.includes('Invitation token is already used')) {
    return t.coachInvite.signup.errors.inviteTokenUsed
  }
  if (haystack.includes('Email is already taken')) {
    return t.coachInvite.signup.errors.emailTaken
  }
  if (haystack.includes('Username is already taken')) {
    return t.coachInvite.signup.errors.usernameTaken
  }
  if (haystack.includes('Invalid username format')) {
    return t.coachInvite.signup.errors.usernameInvalid
  }
  if (haystack.toLowerCase().includes('too many requests')) {
    return t.coachInvite.signup.errors.rateLimited
  }
  return t.coachInvite.signup.errors.generic
}

function CoachInvite() {
  const { t, locale } = useLocale()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const token = (searchParams.get('token') || '').trim()
  const coachEmail = (searchParams.get('coachEmail') || '').trim()
  const hasToken = Boolean(token) && Boolean(coachEmail)

  const deepLink = useMemo(() => {
    if (!hasToken) return 'gymjourney://signup'
    return `gymjourney://coach-invite?token=${encodeURIComponent(token)}&coachEmail=${encodeURIComponent(coachEmail)}`
  }, [hasToken, token])

  const fallbackWebPath = locale === 'fr' ? '/fr/' : '/'

  const isFormValid = useMemo(() => {
    const trimmedEmail = email.trim()
    const trimmedUser = username.trim()
    return (
      EMAIL_REGEX.test(trimmedEmail) &&
      USERNAME_REGEX.test(trimmedUser) &&
      password.length >= 6 &&
      acceptedTerms
    )
  }, [email, username, password, acceptedTerms])

  const fieldValidation = useMemo(() => {
    const v = t.coachInvite.signup.validation
    const trimmedEmail = email.trim()
    const trimmedUser = username.trim()
    const emailOk = EMAIL_REGEX.test(trimmedEmail)
    const userOk = USERNAME_REGEX.test(trimmedUser)
    const passOk = password.length >= 6

    return {
      email: trimmedEmail && !emailOk ? v.emailInvalid : null,
      username: trimmedUser && !userOk ? v.usernameInvalid : null,
      password: password.length > 0 && !passOk ? v.passwordTooShort : null,
      terms: emailOk && userOk && passOk && !acceptedTerms ? v.acceptTerms : null,
    }
  }, [email, username, password, acceptedTerms, t])

  const openApp = () => {
    window.location.href = deepLink
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError('')

    if (!hasToken) {
      return
    }

    if (!acceptedTerms) {
      setSubmitError(t.coachInvite.signup.errors.terms)
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          username: username.trim(),
          password,
          language: locale,
          authMethod: 'email',
          invitationToken: token,
          coachEmail,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        setSubmitError(mapSignupApiError(data, t))
        return
      }



      setIsSuccess(true)
    } catch (error) {
      const isNetworkFailure =
        error instanceof TypeError ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('failed to fetch'))
      setSubmitError(isNetworkFailure ? t.coachInvite.signup.errors.network : t.coachInvite.signup.errors.generic)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="section coach-invite">
      <div className="container coach-invite-container">
        <div className="coach-invite-card">
          {hasToken ? (
            <p className="coach-invite-token-status">{t.coachInvite.tokenDetected}</p>
          ) : (
            <p className="coach-invite-token-warning">{t.coachInvite.tokenMissing}</p>
          )}
          <h1>{t.coachInvite.title}</h1>
          <p className="lead">{t.coachInvite.description}</p>


          <div className="coach-invite-actions">
            <div className="coach-invite-actions-open">
              <button className="btn primary coach-invite-open-app-btn" type="button" onClick={openApp}>
                <img
                  className="badge coach-invite-open-app-logo"
                  src="/assets/logo_no_bg.svg"
                  alt=""
                  width="36"
                  height="36"
                />
                <span>{t.coachInvite.openApp}</span>
              </button>
            </div>
            <div className="coach-invite-actions-stores">
              <a
                className="btn primary coach-invite-store-link"
                href={APP_STORE_URL}
                rel="noopener"
                aria-label="Download Gym Journey on the App Store"
              >
                <img
                  className="badge coach-invite-store-badge"
                  src="/assets/app_store_logo.webp"
                  alt="Download on the App Store"
                  width="120"
                  height="40"
                />
                <span>App Store</span>
              </a>
              <a
                className="btn primary coach-invite-store-link"
                href={PLAY_STORE_URL}
                rel="noopener"
                aria-label="Get Gym Journey on Google Play"
              >
                <img
                  className="badge coach-invite-store-badge coach-invite-store-badge--play"
                  src="/assets/google_play_logo.webp"
                  alt="Get it on Google Play"
                  width="140"
                  height="125"
                />
                <span>Google Play</span>
              </a>
            </div>
          </div>

          {hasToken && !isSuccess ? (
            <form className="coach-invite-signup-form" onSubmit={handleSubmit}>
              <h2>{t.coachInvite.signup.title}</h2>
              <p className="coach-invite-signup-subtitle">{t.coachInvite.signup.subtitle}</p>

              <label htmlFor="invite-email">{t.coachInvite.signup.email}</label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t.coachInvite.signup.emailPlaceholder}
                aria-invalid={fieldValidation.email ? true : undefined}
                aria-describedby={fieldValidation.email ? 'invite-email-error' : undefined}
              />
              {fieldValidation.email ? (
                <p id="invite-email-error" className="coach-invite-field-msg" role="alert">
                  {fieldValidation.email}
                </p>
              ) : null}

              <label htmlFor="invite-username">{t.coachInvite.signup.username}</label>
              <input
                id="invite-username"
                type="text"
                required
                minLength={2}
                maxLength={20}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder={t.coachInvite.signup.usernamePlaceholder}
                aria-invalid={fieldValidation.username ? true : undefined}
                aria-describedby={fieldValidation.username ? 'invite-username-error' : undefined}
              />
              {fieldValidation.username ? (
                <p id="invite-username-error" className="coach-invite-field-msg" role="alert">
                  {fieldValidation.username}
                </p>
              ) : null}

              <label htmlFor="invite-password">{t.coachInvite.signup.password}</label>
              <div className="coach-invite-password-wrap">
                <input
                  id="invite-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t.coachInvite.signup.passwordPlaceholder}
                  autoComplete="new-password"
                  aria-invalid={fieldValidation.password ? true : undefined}
                  aria-describedby={fieldValidation.password ? 'invite-password-error' : undefined}
                />
                <button
                  type="button"
                  className="coach-invite-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? t.coachInvite.signup.hidePassword : t.coachInvite.signup.showPassword}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {fieldValidation.password ? (
                <p id="invite-password-error" className="coach-invite-field-msg" role="alert">
                  {fieldValidation.password}
                </p>
              ) : null}

              <label className="coach-invite-terms">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms((prev) => !prev)}
                  aria-invalid={fieldValidation.terms ? true : undefined}
                  aria-describedby={fieldValidation.terms ? 'invite-terms-hint' : undefined}
                />
                <span>{t.coachInvite.signup.terms}</span>
              </label>
              {fieldValidation.terms ? (
                <p id="invite-terms-hint" className="coach-invite-field-msg" role="alert">
                  {fieldValidation.terms}
                </p>
              ) : null}

              {submitError ? <p className="coach-invite-error">{submitError}</p> : null}

              <button
                className="btn primary coach-invite-submit-btn"
                type="submit"
                disabled={!isFormValid || isSubmitting}
              >
                <span className="coach-invite-submit-label">
                  {isSubmitting ? t.coachInvite.signup.submitting : t.coachInvite.signup.submit}
                </span>
              </button>
            </form>
          ) : null}
          {hasToken && isSuccess ? (
            <div className="coach-invite-success">
              <h2>{t.coachInvite.signup.successTitle}</h2>
              <p>{t.coachInvite.signup.successDescription}</p>
              <p className="coach-invite-success-hint">{t.coachInvite.signup.successDownloadHint}</p>
            </div>
          ) : null}

          <p className="coach-invite-help">
            {t.coachInvite.helpPrefix}{' '}
            <a style={{ textDecoration: 'underline' }} href={`mailto:${t.contact.email}`}>{t.contact.email}</a>
          </p>
        </div>
      </div>
    </section>
  )
}

export default CoachInvite
